import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import debounce from 'lodash.debounce';

import {
  FILES_TAB_MEDIA_TOTALS,
  GET_MEDIA,
  MEDIA_BULK_DELETE,
  MEDIA_DELETE,
  MEDIA_UPDATE,
} from '@atom/graph/media';
import {
  GET_MEDIA_FOLDER_TREE,
  GET_MEDIA_FOLDERS,
  MEDIA_BULK_MOVE,
  MEDIA_FOLDER_CREATE,
  MEDIA_FOLDER_DELETE,
  MEDIA_FOLDER_MOVE,
  MEDIA_FOLDER_UPDATE,
} from '@atom/graph/mediaFolder';
import { useFileDownload } from '@atom/hooks/useFileDownload';
import { useFileUpload } from '@atom/hooks/useFileUpload';
import { Media, MediaConnectionInput, MediaType } from '@atom/types/media';

import { ROOT_FOLDER_ID } from './constants';
import { FilesData, UseFilesTabDataArgs } from './types';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_UPLOAD_WIDTHS = [180, 1440];

const useFilesTabData = (args: UseFilesTabDataArgs) => {
  const { subjectId, subjectType, folderId, mediaType, searchName } = args;

  const [debouncedSearch, setDebouncedSearch] = useState(searchName);
  const debouncedSetSearch = useMemo(
    () =>
      debounce((next: string) => setDebouncedSearch(next), SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    debouncedSetSearch(searchName);
  }, [searchName, debouncedSetSearch]);

  const isSearching = !!debouncedSearch;
  const folderFilter: MediaConnectionInput =
    folderId && !isSearching ? { folderId } : {};

  const folderTreeVars = { subjectId };
  const foldersVars = {
    input: {
      subjectId,
      ...(folderId && !isSearching ? { parentFolderId: folderId } : {}),
      ...(debouncedSearch ? { name: debouncedSearch } : {}),
    },
  };
  const mediaVars = {
    input: {
      parentSubjectIds: [subjectId],
      subjectTypes: [subjectType],
      limit: 250,
      ...folderFilter,
      ...(mediaType ? { type: mediaType } : {}),
      ...(debouncedSearch ? { name: debouncedSearch } : {}),
    },
  };

  const totalsBase: MediaConnectionInput = {
    parentSubjectIds: [subjectId],
    subjectTypes: [subjectType],
    limit: 0,
  };
  const totalsVars = {
    documents: { ...totalsBase, type: MediaType.DOCUMENT },
    images: { ...totalsBase, type: MediaType.IMAGE },
    videos: { ...totalsBase, type: MediaType.VIDEO },
  };

  const folderTreeQuery = useQuery(GET_MEDIA_FOLDER_TREE, {
    variables: folderTreeVars,
    fetchPolicy: 'cache-and-network',
  });

  const foldersQuery = useQuery(GET_MEDIA_FOLDERS, {
    variables: foldersVars,
    fetchPolicy: 'cache-and-network',
    skip: !!mediaType,
  });

  const mediaQuery = useQuery(GET_MEDIA, {
    variables: mediaVars,
    fetchPolicy: 'cache-and-network',
  });

  const totalsQuery = useQuery(FILES_TAB_MEDIA_TOTALS, {
    variables: totalsVars,
    fetchPolicy: 'cache-and-network',
  });

  const folderQueries = [
    { query: GET_MEDIA_FOLDER_TREE, variables: folderTreeVars },
    { query: GET_MEDIA_FOLDERS, variables: foldersVars },
  ];
  const mediaQueries = [
    { query: GET_MEDIA, variables: mediaVars },
    { query: FILES_TAB_MEDIA_TOTALS, variables: totalsVars },
  ];

  const [folderCreate] = useMutation(MEDIA_FOLDER_CREATE, {
    refetchQueries: folderQueries,
    awaitRefetchQueries: true,
  });
  const [folderUpdate] = useMutation(MEDIA_FOLDER_UPDATE, {
    refetchQueries: folderQueries,
    awaitRefetchQueries: true,
  });
  const [folderDelete] = useMutation(MEDIA_FOLDER_DELETE, {
    refetchQueries: [...folderQueries, ...mediaQueries],
    awaitRefetchQueries: true,
  });
  const [folderMove] = useMutation(MEDIA_FOLDER_MOVE, {
    refetchQueries: folderQueries,
    awaitRefetchQueries: true,
  });
  const [mediaBulkMove] = useMutation(MEDIA_BULK_MOVE, {
    refetchQueries: mediaQueries,
    awaitRefetchQueries: true,
  });
  const [mediaUpdate] = useMutation(MEDIA_UPDATE, {
    refetchQueries: [{ query: GET_MEDIA, variables: mediaVars }],
    awaitRefetchQueries: true,
  });
  const [mediaDelete] = useMutation(MEDIA_DELETE, {
    refetchQueries: mediaQueries,
    awaitRefetchQueries: true,
  });
  const [mediaBulkDelete] = useMutation(MEDIA_BULK_DELETE, {
    refetchQueries: mediaQueries,
    awaitRefetchQueries: true,
  });

  const createFolder = (parentFolderId: string, name: string) =>
    folderCreate({
      variables: {
        input: {
          name,
          subjectId,
          ...(parentFolderId && parentFolderId !== ROOT_FOLDER_ID
            ? { parentFolderId }
            : {}),
        },
      },
    });

  const editFolder = (id: string, name: string) =>
    folderUpdate({ variables: { input: { id, name } } });

  const deleteFolder = (id: string) => folderDelete({ variables: { id } });

  const moveFolder = (id: string, destParentFolderId: string) =>
    folderMove({ variables: { input: { id, destParentFolderId } } });

  const moveMedia = (mediaIds: string[], destFolderId: string) =>
    mediaBulkMove({ variables: { input: { mediaIds, destFolderId } } });

  const renameMedia = (id: string, name: string) =>
    mediaUpdate({ variables: { input: { id, name } } });

  const deleteMedia = (id: string) => mediaDelete({ variables: { id } });

  const bulkDeleteMedia = (ids: string[]) =>
    mediaBulkDelete({ variables: { input: { ids } } });

  const [downloadFiles] = useFileDownload();

  const downloadSelected = async (selectedMedia: Media[]) => {
    await downloadFiles(selectedMedia);
  };

  const totals: Partial<Record<MediaType, number>> = {
    [MediaType.DOCUMENT]: totalsQuery.data?.documents?.totalCount ?? 0,
    [MediaType.IMAGE]: totalsQuery.data?.images?.totalCount ?? 0,
    [MediaType.VIDEO]: totalsQuery.data?.videos?.totalCount ?? 0,
  };

  const [uploader, uploadState] = useFileUpload();

  const uploadAndRefetch = async ({
    files,
    widths = DEFAULT_UPLOAD_WIDTHS,
  }: FilesData) => {
    const result = await uploader({
      files,
      subjectId,
      subjectType,
      parentSubjectId: subjectId,
      ...(folderId ? { folderId } : {}),
      widths,
    });
    await Promise.all([mediaQuery.refetch(), totalsQuery.refetch()]);
    return result;
  };

  return {
    folderTree: folderTreeQuery.data?.mediaFolderTree,
    folderTreeLoading: folderTreeQuery.loading,
    folders: foldersQuery.data?.mediaFolders ?? [],
    foldersLoading: foldersQuery.loading,
    media: mediaQuery.data?.media?.media ?? [],
    mediaLoading: mediaQuery.loading,
    totals,
    totalsLoading: totalsQuery.loading,
    createFolder,
    editFolder,
    deleteFolder,
    moveFolder,
    moveMedia,
    renameMedia,
    deleteMedia,
    bulkDeleteMedia,
    downloadSelected,
    uploadAndRefetch,
    uploadLoading: uploadState.loading,
  };
};

export default useFilesTabData;
export type FilesTabData = ReturnType<typeof useFilesTabData>;
